import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Layout from "../../Layout.tsx";
import { getContainerTypes } from "../../../data/containertype";
import { ContainerType } from "../../../types/ContainerType";
import { showModal } from "../../../data/modal";
import Modal from "../../Modal.tsx";
import SettingsMenu from "../Menu";

import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import {
  Close as CloseIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

const ContainerTypeManage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const selectedData = useSelector((state: any) => ({
    containerTypes: state.containerTypes,
  }));

  useEffect(() => {
    dispatch(getContainerTypes() as any);
  }, [dispatch]);

  const { containerTypes } = selectedData;

  const filteredContainerTypes = useMemo(() => {
    const containerTypeData: ContainerType[] = containerTypes?.data ?? [];
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return containerTypeData;
    }

    return containerTypeData.filter((containerType: ContainerType) => {
      const name = containerType.name?.toLowerCase() ?? "";
      const abbreviation = containerType.abbreviation?.toLowerCase() ?? "";
      const teu = containerType.teu?.toString().toLowerCase() ?? "";

      const aliases = Array.isArray((containerType as any).aliases)
        ? (containerType as any).aliases
            .map((alias: string) => alias?.toLowerCase?.() ?? "")
            .join(" ")
        : "";

      return (
        name.includes(normalizedSearchTerm) ||
        abbreviation.includes(normalizedSearchTerm) ||
        aliases.includes(normalizedSearchTerm) ||
        teu.includes(normalizedSearchTerm)
      );
    });
  }, [containerTypes?.data, searchTerm]);

  const formatAliases = (aliases?: string[] | null) => {
    if (!Array.isArray(aliases) || aliases.length === 0) {
      return "";
    }

    return aliases.join(", ");
  };

  return (
    <Layout>
      <Modal />

      <Grid container>
        <Grid item xs={12} sm={2} padding={1}>
          <Grid item component={Paper} sx={{ height: "auto" }}>
            <SettingsMenu activeMenu={"containertype"} />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={10} padding={1}>
          <Grid item component={Paper} sx={{ height: "auto" }}>
            <Grid
              className="settings-page-header"
              item
              xs={12}
              sm={12}
              padding={1}
              container
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("searchContainerType")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm("")}
                          edge="end"
                          title={t("clearSearch")}
                          aria-label={t("clearSearch")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    dispatch(
                      showModal({
                        title: t("addFormTitleByType", {
                          type: t("containerType"),
                        }),
                        description: t("addFormTitleByType", {
                          type: t("containerType"),
                        }),
                        form: "containerType",
                      }) as any
                    );
                  }}
                >
                  {t("addContainerType")}
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} padding={1}>
              <Grid item>
                <TableContainer sx={{ height: "80vh", overflow: "auto" }}>
                  <Table aria-label={t("containerTypes")} stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell/>
                        <TableCell>{t("name")}</TableCell>
                        <TableCell align="center">{t("aliases")}</TableCell>
                        <TableCell align="center">{t("teu")}</TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredContainerTypes.length !== 0 &&
                        filteredContainerTypes.map(
                          (containerType: ContainerType) => (
                            <TableRow
                              key={containerType["@id"] ?? containerType.id}
                              className="js-collection-item"
                              data-object-id={containerType["@id"]}
                            >
                              <TableCell>
                                {`${ containerType.abbreviation ?? "" }`}
                              </TableCell>
                              <TableCell>
                                {`${containerType.name ?? ""}`}
                              </TableCell>

                              <TableCell align="center">
                                {formatAliases((containerType as any).aliases)}
                              </TableCell>

                              <TableCell align="center">
                                {containerType.teu}
                              </TableCell>

                              <TableCell align="right">
                                <IconButton
                                  title={t("editContainerType")}
                                  aria-label={t("editContainerType")}
                                  onClick={() => {
                                    dispatch(
                                      showModal({
                                        title: t("editFormTitleByType", {
                                          type: t("containerType"),
                                        }),
                                        description: t("editContainerType"),
                                        form: "containerType",
                                        formProps: {
                                          containerType: containerType,
                                        },
                                      }) as any
                                    );
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default ContainerTypeManage;